import React, { useMemo } from 'react';

/**
 * Returns a memoized CSRF token from the meta tags of the current page, or null
 * if the token is not found
 */
export function useCSRFToken() {
  return useMemo(() => {
    const param = document.querySelector('meta[name=csrf-param]');
    const token = document.querySelector('meta[name=csrf-token]');
    if (!(param && token)) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          'Expected to find a CSRF token on this page, but could not find any. ' +
            "The token must be available in the document's <head> as the " +
            'following meta tags:' +
            '\n\t<meta name="csrf-param">' +
            '\n\t<meta name="csrf-token">',
        );
      }
      return null;
    }
    return {
      param: param.content,
      token: token.content,
    };
  }, []);
}

/**
 * A form component that includes the CSRF token in the request formdata which
 * is required by Rails when performing certain actions that need to be
 * authorized.
 *
 * @example
 * <CSRFForm url="/path/to/content/archive" className="p-16">
 *   <input name="message" value={message} />
 *   <button type="submit">Archive</button>
 * </CSRFForm>
 */
const CSRFForm = ({ children, method, url, formMethod, ...props }) => {
  const csrfToken = useCSRFToken();
  return (
    <form method={formMethod} action={url} {...props}>
      {csrfToken && (
        <>
          <input hidden readOnly name="_method" value={method} />
          <input
            hidden
            readOnly
            name={csrfToken.param}
            value={csrfToken.token}
          />
        </>
      )}
      {children}
    </form>
  );
};

CSRFForm.defaultProps = {
  /**
   * The HTTP method that Rails expect to be included in the formdata as _method
   */
  method: 'put',
  /**
   * The HTTP method of the form itself. It can be either "post" or "get".
   */
  formMethod: 'post',
};

export default CSRFForm;
